import { Link } from "react-router-dom";
import SEOMeta from "@/components/feature/SEOMeta";
import { OrganizationSchema, WebSiteSchema, WebPageSchema } from "@/components/feature/SchemaOrg";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import VideoCard from "@/components/feature/VideoCard";
import {
  siteConfig,
  categories,
  videos,
  featuredVideos,
  hotRecommendations,
  latestUpdates,
} from "@/mocks/siteData";

export default function Home() {
  const featuredData = featuredVideos.map((id) => videos.find((v) => v.id === id)!).filter(Boolean);
  const hotData = hotRecommendations.map((id) => videos.find((v) => v.id === id)!).filter(Boolean);
  const latestData = latestUpdates.map((id) => videos.find((v) => v.id === id)!).filter(Boolean);

  return (
    <>
      <SEOMeta
        title={siteConfig.name}
        description={siteConfig.description}
        keywords={siteConfig.keywords}
        canonicalUrl={siteConfig.url}
        ogImage={siteConfig.ogImage}
      />
      <OrganizationSchema />
      <WebSiteSchema />
      <WebPageSchema
        title={siteConfig.name}
        description={siteConfig.description}
        url={siteConfig.url}
        breadcrumbItems={[{ name: "首页", item: siteConfig.url }]}
        datePublished="2026-01-01"
        dateModified="2026-08-01"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20cinematic%20composition%20with%20warm%20red%20and%20gold%20gradients%2C%20film%20reel%20textures%2C%20soft%20overlapping%20geometric%20shapes%2C%20elegant%20Chinese%20film%20aesthetic%2C%20modern%20streaming%20platform%20atmosphere%2C%20artistic%20abstract%20background%20with%20cinematic%20light%20leaks%2C%20rich%20warm%20tones%2C%20no%20text&width=1800&height=900&seq=hero-bg-91video&orientation=landscape"
              alt="91视频 - 国产影视在线观看"
              title="91视频 - 国产影视在线观看"
              className="w-full h-full object-cover object-top absolute inset-0"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center px-4 w-full">
              <div className="text-center max-w-3xl w-full">
                <h1 className="text-3xl md:text-5xl font-bold text-white font-heading mb-4 leading-tight">
                  91国产影视 · 经典永流传
                </h1>
                <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
                  汇集91年经典国产影片、热门国产电视剧、精彩国产综艺，免费在线观看，高清流畅，每日更新
                </p>
                <div className="relative max-w-lg mx-auto">
                  <input
                    type="text"
                    placeholder="搜索影片、导演、演员..."
                    className="w-full px-5 py-3.5 pl-12 rounded-full bg-white/95 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 shadow-lg"
                  />
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                  <button className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-primary-500 hover:bg-primary-600 text-white px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                    搜索
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6 text-center">
                浏览分类
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-xl bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer"
                  >
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-white transition-all">
                      <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                    </div>
                    <span className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                      {cat.name}
                    </span>
                    <span className="text-xs text-foreground-500 text-center line-clamp-2">
                      {cat.description}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Content */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                  精选推荐
                </h2>
                <Link
                  to="/category/dianying"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  查看更多 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                {featuredData.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            </div>
          </section>

          {/* Hot Recommendations + Latest Updates */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                      热门推荐
                    </h2>
                    <Link
                      to="/category/dianshiju"
                      className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
                    >
                      更多 <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                    {hotData.map((video) => (
                      <VideoCard key={video.id} {...video} />
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                      最新更新
                    </h2>
                    <Link
                      to="/category/zongyi"
                      className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
                    >
                      更多 <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                    {latestData.map((video) => (
                      <VideoCard key={video.id} {...video} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* All Videos Grid */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-6">
                全部影片
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4">
                {videos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Banner */}
          <section className="relative py-14 md:py-20 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20gradient%20abstract%20background%20with%20soft%20red%20and%20cream%20tones%2C%20cinematic%20film%20strip%20texture%2C%20elegant%20geometric%20pattern%2C%20Chinese%20entertainment%20platform%20atmosphere%2C%20clean%20modern%20design%2C%20subtle%20light%20effects&width=1800&height=600&seq=cta-bg-91video&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top absolute inset-0"
            />
            <div className="absolute inset-0 bg-black/40"></div>
            <div className="relative w-full px-4 md:px-6 max-w-[1400px] mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white font-heading mb-3">
                经典永不褪色，好片即刻开看
              </h2>
              <p className="text-white/75 text-sm md:text-base mb-6 max-w-md mx-auto">
                无论您是想重温经典国产老片，还是追更热门国产剧集，91视频都是您的最佳选择
              </p>
              <Link
                to="/category/dianying"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg"></i>
                开始观看
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}